import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Option, Select, Typography } from "@mui/joy";
import Box from "@mui/joy/Box";
import Checkbox from "@mui/joy/Checkbox";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import { PickersActionBarProps, pickersLayoutClasses } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import React from "react";
import {
  ApproximateTime,
  ExactTime,
  RelativeTime,
  TemporalPrecision,
  TimeInterval,
  TimeType,
} from "../../../../models/human_time";
import { isApproximateTime, isTimeInterval } from "../../../../shared/utils/fact";
import {
  generateApproximateTime,
  generateNoteRelativeTime,
  generateTimeInterval,
  timeToDayjs,
} from "../../../../shared/utils/time";
import { JoyDatePicker } from "./JoyDatePicker";

type DateRangeProps = {
  date?: ApproximateTime | ExactTime | RelativeTime | TimeInterval | null;
  note_datetime?: Dayjs | null;
  onDateChange: (factDate: ApproximateTime | ExactTime | RelativeTime | TimeInterval) => void;
};

// Custom action bar components
interface CustomActionBarProps extends PickersActionBarProps {
  isRelativeToNoteDate: boolean;
  isDisabled: boolean;
  onRelativeChange: (checked: boolean) => void;
}

const CustomActionBarStartDate = ({
  className,
  isRelativeToNoteDate,
  isDisabled,
  onRelativeChange,
}: CustomActionBarProps) => {
  return (
    <Box className={className} sx={{ px: 2, py: 1, borderBottom: 1, borderColor: "divider" }}>
      <Stack direction="column" spacing={1}>
        <Checkbox
          disabled={isDisabled}
          checked={isRelativeToNoteDate}
          onChange={(e) => onRelativeChange(e.target.checked)}
          label="Relative to note's date"
        />
      </Stack>
    </Box>
  );
};

const CustomActionBarEndDate = CustomActionBarStartDate;

// Wrapper components for slots to avoid inline component definitions
const createStartDateActionBar =
  (
    isRelativeToNoteDate: boolean,
    isDisabled: boolean,
    onRelativeChange: (checked: boolean) => void
  ) =>
  (props: PickersActionBarProps) => (
    <CustomActionBarStartDate
      {...props}
      isRelativeToNoteDate={isRelativeToNoteDate}
      isDisabled={isDisabled}
      onRelativeChange={onRelativeChange}
    />
  );

const createEndDateActionBar =
  (
    isRelativeToNoteDate: boolean,
    isDisabled: boolean,
    onRelativeChange: (checked: boolean) => void
  ) =>
  (props: PickersActionBarProps) => (
    <CustomActionBarEndDate
      {...props}
      isRelativeToNoteDate={isRelativeToNoteDate}
      isDisabled={isDisabled}
      onRelativeChange={onRelativeChange}
    />
  );

// Utility function to get views based on precision
const getViewsByPrecision = (
  precision: TemporalPrecision
): readonly ("year" | "month" | "day")[] => {
  switch (precision) {
    case TemporalPrecision.YEAR:
      return ["year"];
    case TemporalPrecision.MONTH:
      return ["year", "month"];
    case TemporalPrecision.DAY:
    default:
      return ["year", "month", "day"];
  }
};

// Utility function to get format based on precision
const getFormatByPrecision = (precision: TemporalPrecision) => {
  switch (precision) {
    case TemporalPrecision.YEAR:
      return "YYYY";
    case TemporalPrecision.MONTH:
      return "MMM YYYY";
    case TemporalPrecision.DAY:
    default:
      return "DD MMM YYYY";
  }
};

// Utility function to determine initial precision
const getInitialPrecision = (date: any): TemporalPrecision => {
  if (isTimeInterval(date)) {
    const interval = date;
    return isApproximateTime(interval.begin)
      ? interval.begin.precision || TemporalPrecision.DAY
      : TemporalPrecision.DAY;
  }
  return isApproximateTime(date) ? date.precision || TemporalPrecision.DAY : TemporalPrecision.DAY;
};

// Utility function to initialize dates from props
const initializeDatesFromProps = (date: any, note_datetime?: Dayjs | null) => {
  const isIntervalInitial = isTimeInterval(date);
  let initialStartDate: Dayjs | null = null;
  let initialEndDate: Dayjs | null = null;
  let initialStartRelative = false;
  let initialEndRelative = false;

  if (isIntervalInitial) {
    const interval = date;
    if (interval.begin.type === TimeType.RELATIVE) {
      initialStartRelative = true;
      initialStartDate = note_datetime || timeToDayjs(null);
    } else {
      initialStartDate = timeToDayjs(interval.begin);
    }
    if (interval.end.type === TimeType.RELATIVE) {
      initialEndRelative = true;
      initialEndDate = note_datetime || timeToDayjs(null);
    } else {
      initialEndDate = timeToDayjs(interval.end);
    }
  } else {
    initialStartDate = timeToDayjs(date);
  }

  return {
    isIntervalInitial,
    initialStartDate,
    initialEndDate,
    initialStartRelative,
    initialEndRelative,
  };
};

export function ComplexDatePicker({ date, note_datetime, onDateChange }: DateRangeProps) {
  const {
    isIntervalInitial,
    initialStartDate,
    initialEndDate,
    initialStartRelative,
    initialEndRelative,
  } = initializeDatesFromProps(date, note_datetime);

  const [isDateRange, setIsDateRange] = React.useState(isIntervalInitial);
  const [isStartRelativeToNoteDate, setIsStartRelativeToNoteDate] =
    React.useState(initialStartRelative);
  const [isEndRelativeToNoteDate, setIsEndRelativeToNoteDate] = React.useState(initialEndRelative);
  const [startDate, setStartDate] = React.useState<Dayjs | null>(initialStartDate);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(initialEndDate);
  const [precision, setPrecision] = React.useState<TemporalPrecision | null>(
    getInitialPrecision(date)
  );

  const handleAddEndDate = () => {
    setIsDateRange(true);
    setEndDate(startDate);
    setIsEndRelativeToNoteDate(false);
  };

  const handleRemoveEndDate = async () => {
    await handleSingleDateChange(startDate, isStartRelativeToNoteDate);
    setIsDateRange(false);
    setEndDate(null);
    setIsEndRelativeToNoteDate(false);
  };

  const handleSingleDateChange = async (
    newStartDate: Dayjs | null,
    startRelative: boolean,
    newPrecision: TemporalPrecision = precision || TemporalPrecision.DAY
  ) => {
    onDateChange(generateApproximateTime(newStartDate, newPrecision));
  };

  const handleDateRangeChange = async (
    newStartDate: Dayjs | null,
    newEndDate: Dayjs | null,
    startRelative: boolean,
    endRelative: boolean,
    newPrecision: TemporalPrecision = precision || TemporalPrecision.DAY
  ) => {
    onDateChange(
      generateTimeInterval(
        startRelative
          ? generateNoteRelativeTime()
          : generateApproximateTime(newStartDate, newPrecision),
        endRelative ? generateNoteRelativeTime() : generateApproximateTime(newEndDate, newPrecision)
      )
    );
  };

  const handleStartRelativeChange = (checked: boolean) => {
    const noteDate = note_datetime || timeToDayjs(null);
    setStartDate(noteDate);
    setIsStartRelativeToNoteDate(checked);
    if (checked) {
      setIsEndRelativeToNoteDate(false);
    }
    handleDateRangeChange(noteDate, endDate, true, false);
  };

  const handleEndRelativeChange = (checked: boolean) => {
    const noteDate = note_datetime || timeToDayjs(null);
    setEndDate(noteDate);
    setIsEndRelativeToNoteDate(checked);
    if (checked) {
      setIsStartRelativeToNoteDate(false);
    }
    handleDateRangeChange(startDate, noteDate, false, true);
  };

  const handlePrecisionChange = (value: TemporalPrecision) => {
    setPrecision(value);
    if (isDateRange) {
      handleDateRangeChange(
        startDate,
        endDate,
        isStartRelativeToNoteDate,
        isEndRelativeToNoteDate,
        value
      );
    } else {
      handleSingleDateChange(startDate, isStartRelativeToNoteDate, value);
    }
  };

  return isDateRange ? (
    <DateRangePickerComponent
      precision={precision}
      startDate={startDate}
      endDate={endDate}
      isStartRelativeToNoteDate={isStartRelativeToNoteDate}
      isEndRelativeToNoteDate={isEndRelativeToNoteDate}
      onPrecisionChange={handlePrecisionChange}
      onStartDateChange={(dayjs) =>
        handleDateRangeChange(dayjs, endDate, isStartRelativeToNoteDate, isEndRelativeToNoteDate)
      }
      onEndDateChange={(dayjs) =>
        handleDateRangeChange(startDate, dayjs, isStartRelativeToNoteDate, isEndRelativeToNoteDate)
      }
      onStartRelativeChange={handleStartRelativeChange}
      onEndRelativeChange={handleEndRelativeChange}
      onRemoveEndDate={handleRemoveEndDate}
    />
  ) : (
    <SingleDatePickerComponent
      precision={precision}
      startDate={startDate}
      isStartRelativeToNoteDate={isStartRelativeToNoteDate}
      onPrecisionChange={setPrecision}
      onDateChange={(e) => handleSingleDateChange(e, isStartRelativeToNoteDate)}
      onAddEndDate={handleAddEndDate}
    />
  );
}

// Separate component for date range picker
interface DateRangePickerComponentProps {
  precision: TemporalPrecision | null;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  isStartRelativeToNoteDate: boolean;
  isEndRelativeToNoteDate: boolean;
  onPrecisionChange: (value: TemporalPrecision) => void;
  onStartDateChange: (dayjs: Dayjs | null) => void;
  onEndDateChange: (dayjs: Dayjs | null) => void;
  onStartRelativeChange: (checked: boolean) => void;
  onEndRelativeChange: (checked: boolean) => void;
  onRemoveEndDate: () => void;
}

const DateRangePickerComponent = ({
  precision,
  startDate,
  endDate,
  isStartRelativeToNoteDate,
  isEndRelativeToNoteDate,
  onPrecisionChange,
  onStartDateChange,
  onEndDateChange,
  onStartRelativeChange,
  onEndRelativeChange,
  onRemoveEndDate,
}: DateRangePickerComponentProps) => {
  return (
    <Box sx={{ mb: 2, mt: 2 }}>
      <Box sx={{ mt: 1, mb: 1 }}>
        <Typography sx={{ mb: 0.75 }} level="title-sm">
          Precision
        </Typography>
        <Select
          sx={{ width: "100px" }}
          value={precision}
          onChange={(e, value) => value && onPrecisionChange(value)}
          defaultValue={TemporalPrecision.DAY}
          disabled={isStartRelativeToNoteDate}
        >
          <Option value={TemporalPrecision.DAY}>Day</Option>
          <Option value={TemporalPrecision.MONTH}>Month</Option>
          <Option value={TemporalPrecision.YEAR}>Year</Option>
        </Select>
      </Box>
      <Stack direction="row" spacing={1}>
        <Box sx={{ width: "calc(50% - 10px - 16px)" }}>
          <JoyDatePicker
            label="Start date"
            value={startDate}
            onChange={onStartDateChange}
            maxDate={endDate || undefined}
            slots={{
              actionBar: createStartDateActionBar(
                isStartRelativeToNoteDate,
                isEndRelativeToNoteDate,
                onStartRelativeChange
              ),
            }}
            views={getViewsByPrecision(precision || TemporalPrecision.DAY)}
            format={getFormatByPrecision(precision || TemporalPrecision.DAY)}
            slotProps={{
              layout: {
                sx: {
                  [`.${pickersLayoutClasses.actionBar}`]: {
                    gridColumn: 2,
                    gridRow: 1,
                  },
                  [`.${pickersLayoutClasses.contentWrapper}`]: {
                    transition: "all .3s ease-out",
                    opacity: isStartRelativeToNoteDate ? 0.3 : 1,
                    pointerEvents: isStartRelativeToNoteDate ? "none" : "initial",
                  },
                },
              },
            }}
          />
        </Box>
        <Box sx={{ width: "calc(50% - 10px - 16px)" }}>
          <JoyDatePicker
            label="End date"
            value={endDate}
            onChange={onEndDateChange}
            minDate={startDate || undefined}
            slots={{
              actionBar: createEndDateActionBar(
                isEndRelativeToNoteDate,
                isStartRelativeToNoteDate,
                onEndRelativeChange
              ),
            }}
            views={getViewsByPrecision(precision || TemporalPrecision.DAY)}
            format={getFormatByPrecision(precision || TemporalPrecision.DAY)}
            slotProps={{
              layout: {
                sx: {
                  [`.${pickersLayoutClasses.actionBar}`]: {
                    gridColumn: 2,
                    gridRow: 1,
                  },
                  [`.${pickersLayoutClasses.contentWrapper}`]: {
                    transition: "all .3s ease-out",
                    opacity: isEndRelativeToNoteDate ? 0.3 : 1,
                    pointerEvents: isEndRelativeToNoteDate ? "none" : "initial",
                  },
                },
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "flex-end" }}>
          <IconButton
            sx={{ width: "32px", height: "32px" }}
            color="danger"
            onClick={onRemoveEndDate}
          >
            <RemoveIcon />
          </IconButton>
        </Box>
      </Stack>
    </Box>
  );
};

// Separate component for single date picker
interface SingleDatePickerComponentProps {
  precision: TemporalPrecision | null;
  startDate: Dayjs | null;
  isStartRelativeToNoteDate: boolean;
  onPrecisionChange: (value: TemporalPrecision | null) => void;
  onDateChange: (date: Dayjs | null) => void;
  onAddEndDate: () => void;
}

const SingleDatePickerComponent = ({
  precision,
  startDate,
  isStartRelativeToNoteDate,
  onPrecisionChange,
  onDateChange,
  onAddEndDate,
}: SingleDatePickerComponentProps) => {
  return (
    <Box sx={{ mb: 2, mt: 2, width: "100%" }}>
      <Stack sx={{ mt: 1 }} direction="row" spacing={1} alignItems="flex-end">
        <Box>
          <Typography sx={{ mb: 0.75 }} level="title-sm">
            Precision
          </Typography>
          <Select
            sx={{ width: "100px" }}
            value={precision}
            onChange={(e, value) => onPrecisionChange(value)}
            defaultValue={TemporalPrecision.DAY}
          >
            <Option value={TemporalPrecision.DAY}>Day</Option>
            <Option value={TemporalPrecision.MONTH}>Month</Option>
            <Option value={TemporalPrecision.YEAR}>Year</Option>
          </Select>
        </Box>
        <Box sx={{ flex: 1 }}>
          <JoyDatePicker
            label="Date"
            value={startDate}
            onChange={onDateChange}
            orientation="portrait"
            views={getViewsByPrecision(precision || TemporalPrecision.DAY)}
            format={getFormatByPrecision(precision || TemporalPrecision.DAY)}
            sx={{ width: "100%" }}
          />
        </Box>
        <IconButton color="primary" onClick={onAddEndDate}>
          <AddIcon />
        </IconButton>
      </Stack>
    </Box>
  );
};
